import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Plus, Tag, Trash2 } from "lucide-react";
import useAuth from "../../../../hooks/useAuth";
import { cn } from "../../../../utils/cn";
import { showToast } from "../../../../utils/showToast";
import {
  createLabel,
  deleteLabel,
  getLabels,
  updateLabel,
} from "../../../../services/helpdeskWorkflowService";
import { isEndpointMissing } from "../../../../services/aiChatService";
import useHelpdeskLabels from "../../../../components/helpdesk/useHelpdeskLabels";
import LabelChip, { labelColorStyles } from "../../../../components/helpdesk/LabelChip";
import {
  DEFAULT_FOLDER_COLOR,
  FOLDER_COLOR_KEYS,
  FOLDER_COLOR_LABELS,
  FOLDER_COLOR_STYLES,
} from "../../../../components/helpdesk/folderColors";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/Input";
import { Textarea } from "../../../../components/ui/Textarea";
import { Modal } from "../../../../components/ui/Modal";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { FieldLabel } from "../../../../components/ui/FieldLabel";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import {
  LABEL_DESCRIPTION_MAX,
  LABEL_NAME_MAX,
  MAX_LABELS,
  idOf,
  isWorkflowSupervisor,
} from "./workflowSchema";
import {
  InlineFormError,
  ListSkeleton,
  LoadErrorBox,
  ReadOnlyNotice,
  RefreshButton,
  SectionHeader,
  normalizeName,
  toastMutationError,
  workflowErrorMessage,
} from "./workflowUi";

// Etiquetas de la mesa de ayuda: marcas libres que el equipo pone a las
// conversaciones (varias por conversación, a diferencia de la carpeta) y que
// macros y reglas agregan o quitan. Crear, editar y borrar es de supervisión;
// el resto del equipo ve el catálogo en solo lectura.

const EMPTY_DRAFT = { name: "", color: DEFAULT_FOLDER_COLOR, description: "" };

/**
 * Validación pura del formulario de etiqueta.
 * @param {{name:string,color:string,description:string}} draft
 * @param {Array} labels  catálogo actual (para el nombre duplicado)
 * @param {string|null} editingId
 * @param {(key:string)=>string} t
 */
export function validateLabelDraft(draft, labels, editingId, t) {
  const errors = {};
  const name = (draft.name || "").trim();
  if (!name) errors.name = t("El nombre es obligatorio.");
  else if (name.length > LABEL_NAME_MAX) errors.name = t("Máximo 40 caracteres.");
  else if (
    labels.some((l) => idOf(l) !== editingId && normalizeName(l.name) === normalizeName(name))
  ) {
    errors.name = t("Ya existe una etiqueta con ese nombre.");
  }
  if ((draft.description || "").trim().length > LABEL_DESCRIPTION_MAX) {
    errors.description = t("Máximo 140 caracteres.");
  }
  return errors;
}

function openCountText(count, t) {
  if (count === 1) return t("1 conversación abierta");
  return t("{{count}} conversaciones abiertas").replace("{{count}}", count);
}

// ── Formulario ───────────────────────────────────────────────────────────────
function LabelFormModal({ open, mode, draft, onChange, onSubmit, onClose, saving, labels, editingId, serverError }) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState({});
  const errors = useMemo(
    () => validateLabelDraft(draft, labels, editingId, t),
    [draft, labels, editingId, t],
  );

  useEffect(() => {
    if (open) setTouched({});
  }, [open]);

  const errorFor = (field) => (touched[field] ? errors[field] : undefined);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    setTouched({ name: true, description: true });
    if (Object.keys(errors).length > 0) return;
    onSubmit();
  };

  const nameLength = (draft.name || "").trim().length;
  const descriptionLength = (draft.description || "").length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={mode === "edit" ? t("Editar etiqueta") : t("Nueva etiqueta")}
      description={t("El color ayuda a reconocerla en la bandeja; el nombre es lo que se lee.")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          {/* Sin `disabled`: al pulsar se revelan los errores de cada campo. */}
          <Button className="hd-brand-solid" onClick={handleSubmit} loading={saving}>
            {mode === "edit" ? t("Guardar cambios") : t("Crear etiqueta")}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <InlineFormError id="label-server-error">{serverError}</InlineFormError>

        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <FieldLabel className="hd-field-label" htmlFor="label-name">
              {t("Nombre")}
            </FieldLabel>
            <span className="text-xs tabular-nums text-fg-subtle">
              {nameLength} / {LABEL_NAME_MAX}
            </span>
          </div>
          <Input
            id="label-name"
            value={draft.name}
            maxLength={LABEL_NAME_MAX}
            invalid={!!errorFor("name")}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
            placeholder={t("Ej: Reclamo de entrega")}
            aria-describedby={errorFor("name") ? "label-name-error" : undefined}
          />
          {errorFor("name") && (
            <p id="label-name-error" className="text-xs text-status-error">
              {errorFor("name")}
            </p>
          )}
        </div>

        <fieldset className="space-y-1.5">
          <legend className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider hd-field-label">
            {t("Color")}
          </legend>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t("Color")}>
            {FOLDER_COLOR_KEYS.map((key) => {
              const active = draft.color === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => onChange({ ...draft, color: key })}
                  className={cn(
                    "inline-flex items-center gap-1.5 h-11 md:h-8 px-3 rounded-full border text-xs font-medium",
                    "transition-colors duration-150 ease-out motion-reduce:transition-none",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                    active
                      ? "border-brand-500 bg-brand-50 text-brand-700"
                      : "border-border bg-bg text-fg-muted hover:bg-bg-muted hover:text-fg",
                  )}
                >
                  <span className={cn("w-2.5 h-2.5 rounded-full", labelColorStyles(key).dot)} aria-hidden="true" />
                  {t(FOLDER_COLOR_LABELS[key])}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <FieldLabel className="hd-field-label" htmlFor="label-description">
              {t("Descripción")}
            </FieldLabel>
            <span
              className={cn(
                "text-xs tabular-nums",
                descriptionLength > LABEL_DESCRIPTION_MAX ? "text-status-error font-semibold" : "text-fg-subtle",
              )}
            >
              {descriptionLength} / {LABEL_DESCRIPTION_MAX}
            </span>
          </div>
          <Textarea
            id="label-description"
            rows={2}
            value={draft.description}
            invalid={!!errorFor("description")}
            onChange={(e) => onChange({ ...draft, description: e.target.value })}
            onBlur={() => setTouched((prev) => ({ ...prev, description: true }))}
            placeholder={t("Opcional. Cuándo usarla, para que todo el equipo la aplique igual.")}
          />
          {errorFor("description") && (
            <p className="text-xs text-status-error">{errorFor("description")}</p>
          )}
        </div>

        {/* Vista previa: el chip tal como se verá en la bandeja. */}
        <div className="flex items-center gap-2 text-xs text-fg-muted">
          <span>{t("Así se verá:")}</span>
          <LabelChip label={{ name: (draft.name || "").trim() || t("Etiqueta"), color: draft.color }} />
        </div>
      </form>
    </Modal>
  );
}

// ── Vista ────────────────────────────────────────────────────────────────────
export default function LabelsSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = isWorkflowSupervisor(user);
  // Caché de sesión de la bandeja: se refresca tras cada cambio para que los
  // chips y el filtro vean la etiqueta nueva sin recargar la página.
  const { reload: reloadSharedLabels } = useHelpdeskLabels();

  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [backendMissing, setBackendMissing] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setBackendMissing(false);
    try {
      const list = await getLabels();
      setLabels([...list].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } catch (err) {
      if (isEndpointMissing(err)) {
        setBackendMissing(true);
        setLabels([]);
      } else {
        setLoadError(workflowErrorMessage(err, t("Revisa tu conexión o vuelve a intentarlo.")));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const afterMutation = () => {
    reloadSharedLabels?.();
  };

  const atCap = labels.length >= MAX_LABELS;

  const openCreate = () => {
    setEditingId(null);
    setDraft({ ...EMPTY_DRAFT });
    setServerError(null);
    setFormOpen(true);
  };

  const openEdit = (label) => {
    setEditingId(idOf(label));
    setDraft({
      name: label.name || "",
      color: FOLDER_COLOR_STYLES[label.color] ? label.color : DEFAULT_FOLDER_COLOR,
      description: label.description || "",
    });
    setServerError(null);
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setServerError(null);
    const payload = {
      name: draft.name.trim(),
      color: draft.color,
      description: draft.description.trim(),
    };
    try {
      if (editingId) {
        const saved = await updateLabel(editingId, payload);
        setLabels((prev) =>
          prev.map((l) => (idOf(l) === editingId ? { ...l, ...payload, ...(saved || {}) } : l)),
        );
        showToast(t("Etiqueta actualizada."), true);
      } else {
        const saved = await createLabel(payload);
        if (saved && idOf(saved)) {
          setLabels((prev) =>
            [...prev, { count: 0, ...saved }].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
          );
        } else {
          await load();
        }
        showToast(t("Etiqueta creada."), true);
      }
      afterMutation();
      setFormOpen(false);
    } catch (err) {
      if (err?.status === 409) {
        // Nombre duplicado (otro usuario la creó mientras tanto) o tope de 50.
        setServerError(
          workflowErrorMessage(
            err,
            t("Ya existe una etiqueta con ese nombre o se alcanzó el tope de 50 etiquetas."),
          ),
        );
      } else {
        toastMutationError(err, t("No se pudo guardar la etiqueta."), t);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { unlabeled_count } = await deleteLabel(idOf(deleteTarget));
      setLabels((prev) => prev.filter((l) => idOf(l) !== idOf(deleteTarget)));
      showToast(
        unlabeled_count === 1
          ? t("Etiqueta eliminada. Se quitó de 1 conversación.")
          : unlabeled_count > 1
          ? t("Etiqueta eliminada. Se quitó de {{count}} conversaciones.").replace(
              "{{count}}",
              unlabeled_count,
            )
          : t("Etiqueta eliminada."),
        true,
      );
      afterMutation();
      setDeleteTarget(null);
    } catch (err) {
      toastMutationError(err, t("No se pudo eliminar la etiqueta."), t);
    } finally {
      setDeleting(false);
    }
  };

  const deleteCount = Number(deleteTarget?.count) || 0;

  return (
    <div className="space-y-4">
      <SectionHeader
        title={t("Etiquetas")}
        description={t(
          "Marcas que el equipo pone a las conversaciones para agruparlas y filtrarlas. Una conversación puede tener varias.",
        )}
        meta={
          !loading && !loadError
            ? t("{{count}} de {{max}} etiquetas").replace("{{count}}", labels.length).replace("{{max}}", MAX_LABELS)
            : null
        }
        actions={
          <>
            <RefreshButton loading={loading} onClick={load} />
            {canEdit && (
              <Button
                className="hd-brand-solid"
                iconLeft={<Plus className="w-4 h-4" aria-hidden="true" />}
                onClick={openCreate}
                disabled={atCap || loading}
                title={atCap ? t("Se alcanzó el tope de 50 etiquetas.") : undefined}
              >
                {t("Nueva etiqueta")}
              </Button>
            )}
          </>
        }
      />

      {!canEdit && (
        <ReadOnlyNotice>
          {t("Solo supervisores y administradores pueden crear, editar o eliminar etiquetas.")}
        </ReadOnlyNotice>
      )}

      {backendMissing && (
        <PendingBackendNotice>
          {t("Las etiquetas están en construcción. Los cambios no se guardarán hasta que estén disponibles.")}
        </PendingBackendNotice>
      )}

      {loading && <ListSkeleton rows={3} />}

      {!loading && loadError && (
        <LoadErrorBox title={t("No se pudieron cargar las etiquetas.")} message={loadError} onRetry={load} />
      )}

      {!loading && !loadError && labels.length === 0 && (
        <div className="rounded-xl border border-border bg-bg">
          <EmptyState
            icon={<Tag aria-hidden="true" />}
            title={t("Todavía no hay etiquetas")}
            description={t(
              "Por ejemplo «Reclamo», «Cambio de dirección» o «Cliente VIP». Se ponen desde el chat, desde una macro o con una regla automática.",
            )}
            action={
              canEdit && !backendMissing ? (
                <Button
                  className="hd-brand-solid"
                  iconLeft={<Plus className="w-4 h-4" aria-hidden="true" />}
                  onClick={openCreate}
                >
                  {t("Crear la primera")}
                </Button>
              ) : null
            }
          />
        </div>
      )}

      {!loading && !loadError && labels.length > 0 && (
        <ul className="rounded-xl border border-border bg-bg divide-y divide-border" aria-label={t("Etiquetas")}>
          {labels.map((label) => {
            const count = Number(label.count) || 0;
            return (
              <li key={idOf(label)} className="flex items-center gap-3 px-[1rem] py-[0.75rem] sm:px-[1.5rem] sm:py-[1rem]">
                <span
                  className={cn("w-2.5 h-2.5 rounded-full shrink-0", labelColorStyles(label.color).dot)}
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-fg truncate">{label.name}</p>
                  {label.description && (
                    <p className="text-xs text-fg-muted truncate" title={label.description}>
                      {label.description}
                    </p>
                  )}
                  {/* En teléfono el recuento baja bajo el nombre: a la derecha
                      le quitaba al nombre casi todo el ancho. */}
                  <p className="sm:hidden text-xs text-fg-muted tabular-nums">{openCountText(count, t)}</p>
                </div>
                <span className="hidden sm:inline text-xs text-fg-muted tabular-nums whitespace-nowrap shrink-0">
                  {openCountText(count, t)}
                </span>
                {canEdit && (
                  <span className="flex items-center gap-0.5 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 h-11 md:h-8"
                      onClick={() => openEdit(label)}
                      aria-label={t("Editar etiqueta {{nombre}}").replace("{{nombre}}", label.name)}
                    >
                      <Pencil className="w-4 h-4" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 h-11 md:h-8 text-status-error hover:bg-status-error-soft"
                      onClick={() => setDeleteTarget(label)}
                      aria-label={t("Eliminar etiqueta {{nombre}}").replace("{{nombre}}", label.name)}
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <LabelFormModal
        open={formOpen}
        mode={editingId ? "edit" : "create"}
        draft={draft}
        onChange={(next) => {
          setDraft(next);
          // El 409 hablaba del nombre anterior: al editar deja de ser cierto.
          if (serverError) setServerError(null);
        }}
        onSubmit={handleSubmit}
        onClose={() => setFormOpen(false)}
        saving={saving}
        labels={labels}
        editingId={editingId}
        serverError={serverError}
      />

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        size="sm"
        title={t("Eliminar etiqueta")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("Cancelar")}
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleting}>
              {t("Eliminar")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg">{t("Vas a eliminar esta etiqueta:")}</p>
        <p className="mt-1 text-sm font-semibold text-fg break-words">{deleteTarget?.name}</p>
        <p className="mt-3 text-sm text-fg-muted">
          {deleteCount > 0
            ? (deleteCount === 1
                ? t("La tiene 1 conversación abierta, que la perderá.")
                : t("La tienen {{count}} conversaciones abiertas, que la perderán.").replace(
                    "{{count}}",
                    deleteCount,
                  ))
            : t("Ninguna conversación abierta la tiene.")}{" "}
          {t("También se quita de las resueltas y de las macros y reglas que la usen. No se puede deshacer.")}
        </p>
      </Modal>
    </div>
  );
}
